import React, { useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import isEmpty from "lodash/isEmpty";

import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Expand from "@mui/icons-material/AddBoxOutlined";
import Collapse from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import Button from "@mui/material/Button";

import { generateTimeOptions, generateDurationOptions, convertToUserTime } from "orion-components/SharedComponents";
import { SelectField } from "orion-components/CBComponents";
import { Translate, getTranslation } from "orion-components/i18n";
import Checkpoints from "./Checkpoints";
import { Delete } from "orion-components/CBComponents/Icons";
import { DateTime } from "luxon";

const statusColorOptions = {
	COMPLETED: "#00CE4F",
	OVERDUE: "#C23907",
	PENDING: "#4DB5F4",
	NOT_STARTED: "#959595"
};

const calculatePatrolTimeFrame = (start, minutesToAdd, timeFormat, locale) => {
	const format = timeFormat === "24-hour" ? "HH:mm" : "hh:mm A";
	let end = moment(start).isValid() ? moment(start) : moment(start, format);
	let formattedStart, start24;

	if (start) {
		formattedStart = convertToUserTime(start, timeFormat, locale);
		start24 = convertToUserTime(start, "24-hour");
		end = end.add(minutesToAdd, "minutes").locale(locale).format(format);
	}
	return { end, formattedStart, start24 };
};

const convertToServerTimeZone = (time24, zone) => {
	// Parse time assuming it's local time today
	const [hour, minute] = time24.split(":").map(Number);
	const local = DateTime.local().set({ hour, minute });

	// Convert to Server time zone
	const serverTime = local.setZone(zone);

	return serverTime.toFormat("HH:mm"); // or 'hh:mm a' for 12-hour format
};

/**
 * Convert server time (in `zone`) to local time, formatting as 12h or 24h.
 * - If input has no AM/PM, it will be parsed as 24h.
 * - If input includes AM/PM (e.g., "3:00 PM"), it will be parsed as 12h.
 */
const convertFromServerToLocalTimeZone = (timeInput, zone, is12Hr = false) => {
	if (!timeInput || typeof timeInput !== "string") {
		console.warn("Invalid time input:", timeInput);
		return "";
	}

	const cleaned = timeInput.trim().toUpperCase();

	// Try 24h first (covers "03:00" and "3:00")
	const tryFormats = [
		{ fmt: "HH:mm", opts: { zone } },
		{ fmt: "H:mm", opts: { zone } }, // tolerate single-digit hour
		{ fmt: "h:mm a", opts: { zone } } // fallback to 12h with AM/PM
	];

	let serverTime = null;
	for (const { fmt, opts } of tryFormats) {
		const dt = DateTime.fromFormat(cleaned, fmt, opts);
		if (dt.isValid) {
			serverTime = dt;
			break;
		}
	}

	if (!serverTime || !serverTime.isValid) {
		console.warn("Failed to parse time:", timeInput);
		return "";
	}

	const localTime = serverTime.setZone(DateTime.local().zoneName);
	const outFmt = is12Hr ? "hh:mm a" : "HH:mm";
	return localTime.toFormat(outFmt);
};

// The timeOptions value is always 24-hour regardless of the time format preference, so this function is used to obtain the actual value based on the label
function getValueForLabel(label, timeOptions) {
	const key = String(label).trim().toUpperCase();
	const hit = timeOptions.find((o) => o.label.toUpperCase() === key);
	return hit ? hit.value : null; // returns the value or null if not found
}

const PatrolCard = ({
	patrol = {},
	dir,
	setCheckpointDialog,
	removeCheckpoint,
	adminRole,
	operatorRole,
	widgetExpanded,
	isTemplate,
	updatePatrolTemplate,
	updatePatrolStatus,
	assignResource,
	completeCheckpoint,
	resources,
	openDeletePatrolDialog,
	timezone
}) => {
	const {
		id,
		name,
		start = "",
		status = "",
		durationInMinutes = 60,
		recurrenceInMinutes,
		checkpoints,
		completionTime = "",
		resourceId
	} = patrol;

	const locale = useSelector((state) => state.i18n.locale);
	//"If the user hasn't set or changed the time format in the Settings app, the Redux value will be unavailable. Since the default format is 12-hour, a fallback has been used accordingly.
	const timeFormatPreference = useSelector((state) => state.appState.global.timeFormat) ?? "12-hour";

	const timeOptions = generateTimeOptions({ timeFormat: timeFormatPreference, locale }) || [];
	const durationOptions = generateDurationOptions();
	const patrolTimeFrame = calculatePatrolTimeFrame(start, durationInMinutes, timeFormatPreference, locale);
	const { end, formattedStart, start24 } = patrolTimeFrame || {};
	const resource = resourceId ? resources.find((resource) => resource.id === resourceId) : null;
	const selectedDurationOption = durationOptions?.find((option) => option.value === durationInMinutes);
	const formattedCompletionTime = completionTime
		? convertToUserTime(completionTime, timeFormatPreference, locale)
		: "";
	const disableResourcesField = resourceId && status !== "NOT_STARTED";
	const requiredPayload = { start, durationInMinutes };
	const canDelete = isTemplate && adminRole;

	const [expanded, setExpanded] = useState(false);
	const allowedStatuses = ["PENDING", "COMPLETED"];

	const styles = {
		card: {
			...(expanded && { backgroundColor: "#28292B" })
		},
		collapsedCard: {
			display: "flex",
			alignItems: "center",
			padding: 7,
			justifyContent: "space-between"
		},
		name: {
			color: "#fff",
			fontSize: 12,
			lineHeight: "unset"
		},
		stat: (value, label) => ({
			color: statusColorOptions[value] || "#B4B8BC",
			fontSize: 10,
			lineHeight: "unset",
			...(label && { color: "fff" })
		}),
		expandIcon: {
			display: "flex",
			cursor: "pointer",
			color: "#fff",
			opacity: 0.5
		},
		divider: {
			backgroundColor: widgetExpanded ? "#383C40" : "rgba(255, 255, 255, 0.1)"
		},
		selectLabel: {
			color: "#B4B8BC",
			fontSize: 10,
			marginBottom: 5
		},
		menuPaperProps: {
			maxHeight: 150,
			overflowY: "auto",
			maxWidth: 90
		},
		ButtonStyles: {
			fontSize: 10,
			padding: "unset",
			textTransform: "none"
		},
		selectField: {
			width: 90,
			...(dir === "rtl" ? { marginLeft: 10 } : { marginRight: 10 })
		},
		startPatrol: {
			fontSize: 10,
			padding: "unset",
			textTransform: "none",
			background: "#4CB5F4",
			color: "#fff",
			opacity: !resourceId ? 0.5 : 1,
			height: 28
		},
		deleteIcon: {
			display: "flex",
			cursor: "pointer",
			color: "#fff",
			opacity: 0.5
		}
	};

	const generateStats = (stat) => {
		let { label, value } = stat || {};
		if (value === "OVERDUE_DURATION" || value === "OVERDUE_START") value = "OVERDUE";
		return value ? (
			<div style={{ display: "flex", flexDirection: "column", margin: "0 7px" }}>
				{label && <Typography style={styles.stat()}>{label}</Typography>}
				<Typography style={styles.stat(value, label)}>
					{getTranslation(`global.profiles.widgets.patrols.patrolCard.${value}`)}
				</Typography>
			</div>
		) : (
			<div />
		);
	};

	const generatePatrolStatusOptions = (status, completionTime, forCheckpoint, patrolId, checkpointId) => {
		if (completionTime) return <div style={styles.stat()}>{completionTime}</div>;
		const buttonLabel =
			!forCheckpoint && ["NOT_STARTED", "OVERDUE_START"].includes(status)
				? "start"
				: status === "PENDING" || status === "OVERDUE_DURATION"
					? "complete"
					: null;

		const handleClick = () => {
			if (forCheckpoint) {
				completeCheckpoint(patrolId, checkpointId);
			} else {
				updatePatrolStatus(buttonLabel, id);
			}
		};

		return buttonLabel ? (
			<Button
				style={styles.startPatrol}
				variant="text"
				color="primary"
				onClick={handleClick}
				disabled={!resourceId}
			>
				<Translate value={`global.profiles.widgets.patrols.patrolCard.${buttonLabel}`} />
			</Button>
		) : (
			""
		);
	};

	const patrolStats = [
		{
			label: getTranslation("global.profiles.widgets.patrols.patrolCard.start"),
			value: formattedStart
		},
		{
			label: getTranslation("global.profiles.widgets.patrols.patrolCard.end"),
			value: end
		},
		{
			label: getTranslation("global.profiles.widgets.patrols.patrolCard.estDuration"),
			value: selectedDurationOption?.label
		},
		{ value: status }
	];

	const openDeviceCheckpointDialog = () => {
		setCheckpointDialog({ id, type: "device" });
	};
	const openAdhocCheckpointDialog = () => {
		setCheckpointDialog({ id, type: "adhoc" });
	};

	return (
		<>
			<div key={id} style={styles.card}>
				<div style={styles.collapsedCard}>
					<div style={{ display: "flex", flexDirection: "column", width: "60%" }}>
						<Typography style={styles.name}>{name}</Typography>
						<Typography style={styles.stat()}>
							{formattedStart &&
								!expanded &&
								`${getTranslation("global.profiles.widgets.patrols.patrolCard.start")}: ${isTemplate
									? convertFromServerToLocalTimeZone(
										formattedStart,
										timezone,
										timeFormatPreference === "12-hour"
									)
									: formattedStart
								}`}
						</Typography>
					</div>
					<div style={{ ...styles.stat(status), margin: "0 10px" }}>
						{!expanded && allowedStatuses.includes(status) &&
							getTranslation(`global.profiles.widgets.patrols.patrolCard.${status}`)}
					</div>
					<div style={styles.stat()}>{operatorRole && !expanded && formattedCompletionTime}</div>
					{canDelete && (
						<div onClick={() => openDeletePatrolDialog({ id, name })} style={styles.deleteIcon}>
							<Delete />
						</div>
					)}
					<div style={styles.expandIcon}>
						{expanded ? (
							<Collapse onClick={() => setExpanded(false)} sx={{ width: 15 }} />
						) : (
							<Expand onClick={() => setExpanded(true)} sx={{ width: 15 }} />
						)}
					</div>
				</div>
				{expanded && (
					<>
						{!isTemplate ? (
							<>
								<div style={{ display: "flex", alignItems: "center", height: 40 }}>
									{patrolStats.map((stat) => generateStats(stat))}
									{generatePatrolStatusOptions(status, formattedCompletionTime)}
								</div>
								<div
									style={{
										...styles.selectField,
										width: widgetExpanded ? "500px" : "auto",
										margin: "10px 7px"
									}}
								>
									<Typography style={styles.selectLabel}>
										{getTranslation("global.profiles.widgets.patrols.patrolCard.assignOfficer")}
									</Typography>
									<SelectField
										id="resources"
										key="resources"
										value={resourceId}
										handleChange={(e) => assignResource(id, e.target.value)}
										dir={dir}
										inline
										variant="outlined"
										formControlProps={{
											style: { margin: 0 }
										}}
										minimizedSelect={true}
										menuPaperProps={styles.menuPaperProps}
										customIcon={disableResourcesField ? "none" : "more"}
										inputProps={{ readOnly: disableResourcesField }}
									>
										{resources.map((resource) => {
											return (
												<MenuItem
													key={resource.id}
													value={resource.id}
													style={{ display: "flex" }}
												>
													{resource.firstName} {resource.lastName}
												</MenuItem>
											);
										})}
									</SelectField>
								</div>
							</>
						) : (
							<>
								<div style={{ display: "flex", padding: 7 }}>
									<div style={styles.selectField}>
										<Typography style={styles.selectLabel}>
											{getTranslation("global.profiles.widgets.patrols.patrolCard.start")}
										</Typography>
										<SelectField
											id="start"
											key="start"
											value={getValueForLabel(
												convertFromServerToLocalTimeZone(
													start24,
													timezone,
													timeFormatPreference === "12-hour"
												),
												timeOptions
											)}
											handleChange={(e) => {
												const start = convertToServerTimeZone(e.target.value, timezone);
												updatePatrolTemplate(id, { ...requiredPayload, start });
											}}
											dir={dir}
											inline
											variant="outlined"
											formControlProps={{
												style: { margin: 0 }
											}}
											minimizedSelect={true}
											menuPaperProps={styles.menuPaperProps}
											inputProps={{ readOnly: !adminRole }}
										>
											{timeOptions.map((option) => {
												return (
													<MenuItem
														key={option.value}
														value={option.value}
														style={{ display: "flex" }}
													>
														{option.label}
													</MenuItem>
												);
											})}
										</SelectField>
									</div>
									<div style={{ ...styles.selectField, width: 66 }}>
										<Typography style={styles.selectLabel}>
											{getTranslation("global.profiles.widgets.patrols.patrolCard.estDuration")}
										</Typography>
										<SelectField
											id="durationInMinutes"
											key="durationInMinutes"
											value={durationInMinutes}
											handleChange={(e) =>
												updatePatrolTemplate(id, {
													...requiredPayload,
													durationInMinutes: e.target.value
												})
											}
											dir={dir}
											inline
											variant="outlined"
											formControlProps={{
												style: { margin: 0 }
											}}
											minimizedSelect={true}
											menuPaperProps={styles.menuPaperProps}
											inputProps={{ readOnly: !adminRole }}
										>
											{durationOptions.map((option) => {
												return (
													<MenuItem
														key={option.value}
														value={option.value}
														style={{ display: "flex" }}
													>
														{getTranslation(
															`global.profiles.widgets.patrols.patrolCard.${option.label}`
														)}
													</MenuItem>
												);
											})}
										</SelectField>
									</div>
									{isTemplate && (
										<div style={{ ...styles.selectField, width: 66 }}>
											<Typography style={styles.selectLabel}>
												{getTranslation(
													"global.profiles.widgets.patrols.patrolCard.recurrence"
												)}
											</Typography>
											<SelectField
												id="recurrenceInMinutes"
												key="recurrenceInMinutes"
												value={recurrenceInMinutes}
												handleChange={(e) =>
													updatePatrolTemplate(id, {
														...requiredPayload,
														recurrenceInMinutes: e.target.value
													})
												}
												dir={dir}
												inline
												variant="outlined"
												formControlProps={{
													style: { margin: 0 }
												}}
												minimizedSelect={true}
												menuPaperProps={styles.menuPaperProps}
												inputProps={{ readOnly: !adminRole }}
											>
												<MenuItem key={"none"} value={null} style={{ display: "flex" }}>
													{getTranslation(`global.profiles.widgets.patrols.patrolCard.none`)}
												</MenuItem>
												{durationOptions.map((option) => {
													return (
														<MenuItem
															key={option.value}
															value={option.value}
															style={{ display: "flex" }}
														>
															{getTranslation(
																`global.profiles.widgets.patrols.patrolCard.${option.label}`
															)}
														</MenuItem>
													);
												})}
											</SelectField>
										</div>
									)}
								</div>
							</>
						)}
						<Checkpoints
							checkpoints={checkpoints}
							resourceId={resourceId}
							dir={dir}
							patrolId={id}
							removeCheckpoint={removeCheckpoint}
							adminRole={adminRole}
							operatorRole={operatorRole}
							statusColorOptions={statusColorOptions}
							isTemplate={isTemplate}
							timeFormat={timeFormatPreference}
							locale={locale}
							resource={resource}
							generatePatrolStatusOptions={generatePatrolStatusOptions}
						/>
						{adminRole && isTemplate && (
							<div
								style={{ display: "flex", justifyContent: "space-between", padding: 7, marginTop: 10 }}
							>
								<Button
									style={styles.ButtonStyles}
									variant="text"
									color="primary"
									onClick={openDeviceCheckpointDialog}
								>
									<Translate value="global.profiles.widgets.patrols.patrolCard.addDeviceCheckpoint" />
								</Button>
								<Button
									style={styles.ButtonStyles}
									variant="text"
									color="primary"
									onClick={openAdhocCheckpointDialog}
								>
									<Translate value="global.profiles.widgets.patrols.patrolCard.addAdhocCheckpoint" />
								</Button>
							</div>
						)}
					</>
				)}
			</div>
			<Divider style={styles.divider} />
		</>
	);
};

export default PatrolCard;
